import { t as getFixedT } from "@/i18n";
import { BRAND } from "@/lib/brand";
import type { SupportedLanguage } from "@/lib/site";
import "./Hero.css";

interface HeroProps {
  lang: SupportedLanguage;
}

/**
 * #hero — 09-landing-page.md §2. The only `<h1>` on the page. The illustration is an inline SVG
 * (no network request, so it never delays LCP/adds CLS — explicit width/height + `aspect-ratio`)
 * with a real descriptive `aria-label` (§5.4 "not decorative alt='' unless truly decorative") since
 * there's no photography asset yet (§9 assumption #1) — swap for a real `<img>`/`srcset` once one
 * lands, structure stays the same.
 */
export function Hero({ lang }: HeroProps) {
  const t = getFixedT(lang);

  return (
    <section id="hero" className="fa-hero" aria-label={t("hero.sectionLabel")}>
      <div className="fa-hero__inner">
        <div className="fa-hero__copy">
          <p className="fa-hero__eyebrow">{t("hero.eyebrow")}</p>
          <h1 className="fa-hero__title">{t("hero.headline")}</h1>
          <p className="fa-hero__subtitle">{t("hero.subtitle")}</p>
          <div className="fa-hero__actions">
            <a href="#lead-form" className="fa-btn fa-btn--primary fa-btn--lg">
              {t("hero.cta")}
            </a>
            <a
              href={`tel:${BRAND.phoneE164}`}
              className="fa-hero__secondary-link"
            >
              {t("hero.secondaryHint")} <bdi>{BRAND.phoneDisplay}</bdi>
            </a>
          </div>
        </div>
        <div className="fa-hero__media">
          <HeroIllustration label={t("hero.illustrationAlt")} />
        </div>
      </div>
    </section>
  );
}

function HeroIllustration({ label }: { label: string }) {
  return (
    <svg
      role="img"
      aria-label={label}
      className="fa-hero__illustration"
      viewBox="0 0 480 360"
      width="480"
      height="360"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect width="480" height="360" rx="24" fill="var(--color-primary-50)" />
      <circle
        className="fa-hero__shape fa-hero__shape--float-a"
        cx="392"
        cy="72"
        r="46"
        fill="var(--color-accent-500)"
        opacity="0.18"
      />
      <circle
        className="fa-hero__shape fa-hero__shape--float-b"
        cx="70"
        cy="290"
        r="60"
        fill="var(--color-primary-400)"
        opacity="0.16"
      />
      <rect
        className="fa-hero__shape fa-hero__shape--card"
        x="140"
        y="120"
        width="200"
        height="140"
        rx="16"
        fill="var(--color-neutral-0)"
      />
      <rect
        x="164"
        y="150"
        width="152"
        height="14"
        rx="7"
        fill="var(--color-primary-100)"
      />
      <rect
        x="164"
        y="178"
        width="120"
        height="14"
        rx="7"
        fill="var(--color-primary-100)"
      />
      <rect
        x="164"
        y="206"
        width="90"
        height="14"
        rx="7"
        fill="var(--color-accent-500)"
        opacity="0.6"
      />
      <path
        className="fa-hero__shape fa-hero__shape--cap"
        d="M240 60 L320 96 L240 132 L160 96 Z"
        fill="var(--color-primary-600)"
      />
      <path
        d="M190 108 V138 C190 150 213 160 240 160 C267 160 290 150 290 138 V108"
        fill="none"
        stroke="var(--color-primary-600)"
        strokeWidth="6"
        strokeLinecap="round"
      />
      <line
        className="fa-hero__shape fa-hero__shape--tassel"
        x1="320"
        y1="96"
        x2="320"
        y2="140"
        stroke="var(--color-primary-800)"
        strokeWidth="4"
        strokeLinecap="round"
      />
    </svg>
  );
}
